import { format } from "date-fns";
import { RefreshCw, ListFilter, ChevronDown, ChevronRight, Info } from "lucide-react";
import React, { useEffect, useMemo, useState, useContext } from "react";
import { Accordion, AccordionContext, useAccordionToggle } from "react-bootstrap";
import { fetchWithAuth } from "../../../../../api-client/APIClient";
import {
  CateogryScopeMap,
  CommonModelToggle,
  CommonModelSyncStatus,
  CommonModelSyncStatusType,
  PauseReason,
  LinkedAccount,
  CommonModelScopeMap,
  AuthType,
  LinkedAccountCondition,
} from "../../../../../models/Entities";
import EmptyStateWrapper from "../../../../shared/EmptyStateWrapper";

import MergeTable from "../../../../shared/MergeTable";
import type { Props as SyncHistoryProps } from "./SyncHistory";
import {
  getLinkedAccountCommonModelTogglesMap,
  createScopeMap,
} from "../../../../../api-client/APIClient";
import { showErrorToast, showSuccessToast } from "../../../../shared/Toasts";
import ScopesTable from "../data/ScopesTable";
import { Schemas } from "../../../configuration/common-models/CommonModelToggleTypes";
import { ScopeStatuses } from "../../../../../constants";
import TableSkeletonLoader from "../../../shared/TableSkeletonLoader";
import {
  Badge,
  Checkbox,
  Tooltip,
  Text,
  Card,
  Menu,
  MenuItem,
  ButtonVariant,
} from "@merge-api/merge-javascript-shared";
import { SortedModelsMap } from "../../../configuration/common-models/CommonModelUtils";
import { EnabledAction } from "../../../../../models/CommonModel";
import { BaseColor } from "@merge-api/merge-javascript-shared/dist/designSystem/types";

const SCOPE_PERMISSION_ERROR =
  "Only Admins can update scopes. Ask an Admin in your organization to make this change.";

const getScopeUpdateErrorMessage = async (response: Response | undefined) => {
  if (response?.status !== 403) {
    return "Unable to update Scope";
  }
  const data = await response.json().catch(() => undefined);
  return data?.non_field_errors?.[0] ?? SCOPE_PERMISSION_ERROR;
};

const getSyncStatusBadge = (
  syncStatus: CommonModelSyncStatusType,
  scopesText: string,
  pauseReason?: PauseReason | null,
  syncReason?: string | null,
) => {
  let color;
  let icon;
  let text;
  let title;

  switch (syncStatus) {
    case CommonModelSyncStatusType.SYNCING:
      color = "blue";
      icon = <RefreshCw className="rotating m-0 p-0" size={10} />;
      text = "Syncing";
      title = syncReason;
      break;

    case CommonModelSyncStatusType.DONE:
      color = "green";
      text = "Done";
      break;

    case CommonModelSyncStatusType.PARTIALLY_SYNCED:
      color = "yellow";
      text = "Partially synced";
      title = "One or more fields for this model failed to sync.";
      break;

    case CommonModelSyncStatusType.FAILED:
      color = "red";
      text = "Failed";
      break;

    case CommonModelSyncStatusType.DISABLED:
      if (scopesText === ScopeStatuses.WRITE) {
        return (
          <Tooltip placement="top" title="Write-only models do not sync data">
            <Badge color="gray">Disabled</Badge>
          </Tooltip>
        );
      } else {
        color = "gray";
        text = "Disabled";
        break;
      }

    case CommonModelSyncStatusType.PAUSED:
      if (pauseReason && pauseReason === PauseReason.PRICING_PLAN_LIMIT) {
        color = "gray";
        text = "Paused";
        title = "Please upgrade your plan to continue syncing data from this account.";
        break;
      } else {
        color = "gray";
        text = "Paused";
        title =
          "Syncing for this account has been paused due to inactivity or unsuccessful sync attempts. To resume automated syncing, ensure that the account’s credentials are valid and press 'Resync all'";
        break;
      }
    default:
      color = "green";
      text = "Done";
      break;
  }
  return (
    <Badge
      color={color as BaseColor}
      className="flex flex-row items-center justify-center gap-x-1.5 whitespace-nowrap"
    >
      {icon}
      <span>{text}</span>
      {title && (
        <Tooltip title={title} className="flex items-center">
          <Info size={12} className="text-gray-40" />
        </Tooltip>
      )}
    </Badge>
  );
};

const getScopesText = (scopes: string[]) => {
  if (!scopes || scopes.length == 0) {
    return ScopeStatuses.DISABLED;
  } else if (scopes.includes("ENABLED_ACTION_WRITE") && scopes.includes("ENABLED_ACTION_READ")) {
    return ScopeStatuses.READ_WRITE;
  } else if (scopes.includes("ENABLED_ACTION_WRITE")) {
    return ScopeStatuses.WRITE;
  } else {
    return ScopeStatuses.READ;
  }
};

const SyncHistoryExpandableRow = ({
  syncStatus,
  linkedAccount,
  scopes,
  numberOfAppliedFilters,
}: {
  syncStatus: CommonModelSyncStatus;
  linkedAccount: LinkedAccount | null;
  scopes: CommonModelScopeMap;
  isOverviewPage: boolean;
  numberOfAppliedFilters: number | undefined;
}) => {
  return (
    <>
      <tr key={syncStatus.model_id}>
        <td>
          <div className="flex flex-row items-center gap-x-1 whitespace-nowrap">
            <Text variant="h6">{syncStatus.model_name}</Text>
            {numberOfAppliedFilters !== undefined && numberOfAppliedFilters > 0 && (
              <Tooltip
                title={
                  numberOfAppliedFilters === 1
                    ? "Selective Sync filter has been applied to this model"
                    : "Selective Sync filters have been applied to this model"
                }
              >
                <ListFilter className="text-gray-40" size="16" />
              </Tooltip>
            )}
          </div>
        </td>
        <td>
          <Text className="text-gray-40">{getScopesText(scopes.actions)}</Text>
        </td>
        <td>
          {getSyncStatusBadge(
            syncStatus.status,
            getScopesText(scopes.actions),
            linkedAccount?.pause_reason,
            syncStatus.sync_reason,
          )}
        </td>
        <td>
          {syncStatus.last_sync_start ? (
            <Text>{format(new Date(syncStatus.last_sync_start), "MMM dd, hh:mm a")}</Text>
          ) : (
            <Text className="text-gray-40">—</Text>
          )}
        </td>
        <td>
          {!(linkedAccount?.is_test_account ?? false) && syncStatus.next_sync_start ? (
            <Text>{format(new Date(syncStatus.next_sync_start), "MMM dd, hh:mm a")}</Text>
          ) : (
            <Text className="text-gray-40">—</Text>
          )}
        </td>
        <td></td>
      </tr>
    </>
  );
};

// TODO @vnarayanam ~ remove this deprecated code when scopes refactor is fully launched ~ https://app.asana.com/0/1205900822182079/1206311452057006
const SyncHistoryExpandableRowWithFieldScopes = ({
  syncStatus,
  linkedAccount,
  scopes,
  commonModels,
  commonModelName,
  updateEnabledStatus,
  numberOfAppliedFilters,
}: {
  syncStatus: CommonModelSyncStatus;
  linkedAccount: LinkedAccount | null;
  scopes: CommonModelScopeMap;
  commonModels: SortedModelsMap;
  commonModelName: string;
  numberOfAppliedFilters: number | undefined;
  updateEnabledStatus: (
    modelName: string,
    fieldName: string | undefined,
    enabledActions: string[],
    shouldResetOverrideActions: boolean,
    shouldResetFieldOverride: boolean,
    onFetch: () => void,
  ) => void;
}) => {
  const currentEventKey = useContext(AccordionContext);
  const isTestAccount = !!linkedAccount?.is_test_account;
  const isSftpAccount = linkedAccount?.auth_type === AuthType.SFTP;
  const isQBDAccount = linkedAccount?.auth_type === AuthType.WEB_CONNECTOR;

  const changeState = (
    modelName: string,
    fieldName: string | null,
    enabledActions: string[],
    shouldResetOverrideActions: boolean,
    shouldResetFieldOverride: boolean,
  ) => {
    const onFetch = () => {
      showSuccessToast("Successfully updated scope");
    };
    const relatingField = (fieldName && commonModels[modelName]?.fields?.[fieldName]) || undefined;
    const relatedModelName = relatingField?.related_to;
    const relatedModel = !relatedModelName ? undefined : commonModels[relatedModelName];
    const isRelatedToSubobject = relatedModel && !relatedModel.has_endpoints;
    const shouldToggleRelatedModelInsteadOfField =
      relatedModelName && isRelatedToSubobject && relatedModel.name !== modelName;
    const transmittedFieldName = shouldToggleRelatedModelInsteadOfField
      ? undefined
      : fieldName ?? undefined;
    const modelToUpdate = shouldToggleRelatedModelInsteadOfField ? relatedModelName : modelName;
    updateEnabledStatus(
      modelToUpdate,
      transmittedFieldName,
      enabledActions,
      shouldResetOverrideActions,
      shouldResetFieldOverride,
      onFetch,
    );
  };

  const clickHandler = (scopeKey: string, enabledActions: string[]) => {
    const shouldResetOverrideActions = scopeKey == ScopeStatuses.RESET;
    changeState(commonModelName, null, enabledActions, shouldResetOverrideActions, false);
  };

  const isLinkedActionOverridesPresent = scopes.linkedAccountActionOverrides?.length > 0 || false;
  const isDisabled = !scopes.actions || scopes.actions.length == 0;
  const isOpen = currentEventKey == syncStatus.model_id;
  const decoratedOnClick = useAccordionToggle(syncStatus.model_id);

  return (
    <>
      <tr
        className={
          !isDisabled
            ? "hover:cursor-pointer active:cursor-pointer focus:cursor-pointer"
            : undefined
        }
        onClick={decoratedOnClick}
      >
        <td>
          <div className="flex flex-row items-center gap-x-1 whitespace-nowrap">
            <Text variant="h6">{syncStatus.model_name}</Text>

            {numberOfAppliedFilters !== undefined && numberOfAppliedFilters > 0 && (
              <Tooltip
                title={
                  numberOfAppliedFilters === 1
                    ? "Selective Sync filter has been applied to this model"
                    : "Selective Sync filters have been applied to this model"
                }
              >
                <ListFilter className="text-gray-40" size="12" />
              </Tooltip>
            )}
          </div>
        </td>
        <td>
          <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
            <Menu
              ButtonProps={{
                variant: ButtonVariant.TextBlack,
                children: (
                  <div className={`flex items-center gap-x-1 ${isDisabled ? "text-gray-40" : ""}`}>
                    {getScopesText(scopes.actions)}
                    <ChevronDown size={16} />
                  </div>
                ),
              }}
              menuPlacement="bottom-start"
            >
              {/* Checkbox rows use div to prevent menu auto-close */}
              <div className="border-b border-gray-10">
                <div className="px-3 py-2 hover:bg-gray-0 cursor-pointer">
                  <Checkbox
                    label={ScopeStatuses.READ}
                    checked={scopes.actions.includes(EnabledAction.READ)}
                    onChange={() => {
                      const actionsToEnabled = scopes.actions.includes(EnabledAction.WRITE)
                        ? [EnabledAction.WRITE]
                        : [];
                      const wasPrevChecked = scopes.actions.includes(EnabledAction.READ);
                      if (!wasPrevChecked) {
                        actionsToEnabled.push(EnabledAction.READ);
                      }
                      clickHandler(ScopeStatuses.READ, actionsToEnabled);
                    }}
                  />
                </div>
                {scopes.capabilities.includes(EnabledAction.WRITE) && (
                  <div className="px-3 py-2 hover:bg-gray-0 cursor-pointer">
                    <Checkbox
                      label={ScopeStatuses.WRITE}
                      checked={scopes.actions.includes(EnabledAction.WRITE)}
                      onChange={() => {
                        const actionsToEnabled = scopes.actions.includes(EnabledAction.READ)
                          ? [EnabledAction.READ]
                          : [];
                        const wasPrevChecked = scopes.actions.includes(EnabledAction.WRITE);
                        if (!wasPrevChecked) {
                          actionsToEnabled.push(EnabledAction.WRITE);
                        }
                        clickHandler(ScopeStatuses.WRITE, actionsToEnabled);
                      }}
                    />
                  </div>
                )}
              </div>
              {/* Reset option uses MenuItem to close menu on click */}
              <MenuItem
                onClick={() => clickHandler(ScopeStatuses.RESET, [])}
                disabled={!isLinkedActionOverridesPresent}
                className="mt-2"
              >
                <Text>Reset to org default</Text>
              </MenuItem>
            </Menu>
            {isLinkedActionOverridesPresent && (
              <Tooltip placement="top" title="Override of your organization's default setting">
                <Text className="italic text-yellow-60 ml-1">Overridden</Text>
              </Tooltip>
            )}
          </div>
        </td>
        <td>
          {getSyncStatusBadge(
            syncStatus.status,
            getScopesText(scopes.actions),
            linkedAccount?.pause_reason,
            syncStatus.sync_reason,
          )}
        </td>
        <td>
          {syncStatus.last_sync_start ? (
            <Text>{format(new Date(syncStatus.last_sync_start), "MMM dd, hh:mm a")}</Text>
          ) : (
            <Text className="text-gray-40">—</Text>
          )}
        </td>
        {!isTestAccount && !isSftpAccount && !isQBDAccount && (
          <td>
            {!(linkedAccount?.is_test_account ?? false) && syncStatus.next_sync_start ? (
              <Text>{format(new Date(syncStatus.next_sync_start), "MMM dd, hh:mm a")}</Text>
            ) : (
              <Text className="text-gray-40">—</Text>
            )}
          </td>
        )}
        <td className="text-right pr-6 align-middle">
          <ChevronRight
            size={16}
            className={`text-gray-40 transition-transform duration-300 ${
              isOpen && !isDisabled ? "rotate-90" : ""
            }`}
          />
        </td>
      </tr>
      {/* dont render scopes table if model is disabled */}
      {!isDisabled && (
        <td colSpan={6} className="border-top-0 pt-0 pb-0 hover:bg-white">
          <Accordion.Collapse eventKey={syncStatus.model_id}>
            <ScopesTable
              key={commonModelName}
              linkedAccountScopes={scopes}
              commonModelName={commonModelName}
              changeState={changeState}
              hideScopesToggle={isSftpAccount}
            />
          </Accordion.Collapse>
        </td>
      )}
    </>
  );
};

type Props = Pick<SyncHistoryProps, "linkedAccount"> & {
  /**
   * Data for the table - array of sync statuses, either fetched and
   * empty or full, or unfetched and represented by `null`.
   */
  syncStatuses: CommonModelSyncStatus[] | null;
  resyncButton?: any;
  isOverviewPage?: boolean;
  includeLinkedAccountScopes?: boolean;
  schemas?: Schemas | null;
  commonModels?: SortedModelsMap | null;
  remoteLinkedAccountConditions?: LinkedAccountCondition[];
  isOnboardingPage?: boolean;
};

/**
 * Shows a table of the syncs that have happened in the past - automatically polls for new
 * data once every three seconds to keep it up to date.
 */
const SyncHistoryTable = ({
  linkedAccount,
  syncStatuses,
  resyncButton,
  isOverviewPage = false,
  includeLinkedAccountScopes = false,
  commonModels = null,
  remoteLinkedAccountConditions,
  isOnboardingPage = false,
}: Props) => {
  const isTestAccount = !!linkedAccount?.is_test_account;
  const isFirstSyncComplete = !!linkedAccount?.completed_at;
  const isSftpAccount = linkedAccount?.auth_type === AuthType.SFTP;
  const isQBDAccount = linkedAccount?.auth_type === AuthType.WEB_CONNECTOR;
  const alphabetizedSyncStatuses = useMemo(() => {
    syncStatuses?.sort((a, b) => a.model_name.localeCompare(b.model_name));
    return syncStatuses;
  }, [syncStatuses]);

  const [linkedAccountLevelCommonModelScopes, setLinkedAccountLevelCommonModelScopes] = useState<
    CateogryScopeMap | undefined
  >(undefined);

  useEffect(() => {
    if (!linkedAccount) {
      return;
    }
    getLinkedAccountCommonModelTogglesMap(linkedAccount, setLinkedAccountLevelCommonModelScopes);
  }, [linkedAccount]);

  const updateEnabledStatus = (
    modelName: string,
    fieldName: string | undefined,
    enabledActions: string[],
    shouldResetOverrideActions: boolean,
    shouldResetFieldOverride: boolean,
    onFetch: () => void,
  ) => {
    if (!linkedAccount) {
      return;
    }
    const updatedScope = {
      enabled_actions: enabledActions,
      ...(fieldName && { field: fieldName }),
      name: modelName,
      reset_linked_account_action_override: shouldResetOverrideActions,
      reset_linked_account_field_override: shouldResetFieldOverride,
    };
    fetchWithAuth({
      path: `integrations/common-model-toggles/${linkedAccount.category}?linked_account_id=${linkedAccount.id}`,
      method: "PATCH",
      body: updatedScope,
      onResponse: (data: CommonModelToggle[]) => {
        setLinkedAccountLevelCommonModelScopes(createScopeMap(data));
        onFetch();
      },
      onError: async (response) => {
        showErrorToast(await getScopeUpdateErrorMessage(response));
      },
    });
  };

  const syncStatusRows = includeLinkedAccountScopes
    ? linkedAccountLevelCommonModelScopes &&
      commonModels &&
      alphabetizedSyncStatuses
        ?.filter((syncStatus) =>
          Object.keys(linkedAccountLevelCommonModelScopes).includes(syncStatus.model_name),
        )
        .map((syncStatus, index) => {
          const numberOfAppliedFilters = remoteLinkedAccountConditions?.filter(
            (condition) => condition.common_model === syncStatus.model_name,
          ).length;

          return (
            <SyncHistoryExpandableRowWithFieldScopes // toggles enabled
              key={syncStatus.model_id}
              syncStatus={syncStatus}
              linkedAccount={linkedAccount}
              scopes={linkedAccountLevelCommonModelScopes![syncStatus.model_name] || []}
              commonModels={commonModels}
              commonModelName={syncStatus.model_name}
              updateEnabledStatus={updateEnabledStatus}
              numberOfAppliedFilters={numberOfAppliedFilters}
            />
          );
        })
    : linkedAccountLevelCommonModelScopes &&
      alphabetizedSyncStatuses?.map((syncStatus) => {
        const numberOfAppliedFilters = remoteLinkedAccountConditions?.filter(
          (condition) => condition.common_model === syncStatus.model_name,
        ).length;
        return (
          <SyncHistoryExpandableRow // sync history page no toggles enabled
            key={syncStatus.model_id} // Added key for consistency
            syncStatus={syncStatus}
            linkedAccount={linkedAccount}
            scopes={linkedAccountLevelCommonModelScopes[syncStatus.model_name] || []}
            isOverviewPage={isOverviewPage}
            numberOfAppliedFilters={numberOfAppliedFilters}
          />
        );
      });

  const header = (
    <>
      <th scope="col">Model</th>
      <th scope="col">Scope</th>
      <th scope="col">Status</th>
      <th scope="col" colSpan={isTestAccount ? 2 : 1}>
        {isSftpAccount || isQBDAccount ? "Last updated" : "Last sync start"}
      </th>
      {!isTestAccount && !isSftpAccount && !isQBDAccount && (
        <th scope="col">
          Next sync{" "}
          <Tooltip title="This is an approximation. Many factors affect the next start time.">
            <Info size={12} />
          </Tooltip>
        </th>
      )}
    </>
  );

  const content =
    linkedAccount && !isFirstSyncComplete ? (
      <tr>
        <td colSpan={12} className="p-0">
          <EmptyStateWrapper isTable title="Account not yet linked" />
        </td>
      </tr>
    ) : linkedAccount?.pause_reason === PauseReason.PLAN_LIMIT_REACHED ? (
      <tr>
        <td colSpan={12} className="p-0">
          <EmptyStateWrapper isTable title="No data synced" />
        </td>
      </tr>
    ) : syncStatuses === null || syncStatusRows === undefined ? (
      <TableSkeletonLoader cols={5} />
    ) : (
      (!!syncStatuses.length && syncStatusRows) || (
        <tr>
          <td colSpan={12} className="p-0">
            <EmptyStateWrapper isTable title="No sync events" />
          </td>
        </tr>
      )
    );

  return (
    <div className="flex flex-col">
      {!isOnboardingPage && (
        <div className="flex flex-row items-center justify-between mb-5">
          <Text variant="h3">{isSftpAccount ? "SFTP data" : "Data sync"}</Text>
          <div>{resyncButton}</div>
        </div>
      )}
      <Card className="flex flex-col mb-9">
        <Accordion>
          <MergeTable header={header} content={content} hasMarginBottom={false} noBoxShadow />
        </Accordion>
      </Card>
    </div>
  );
};

export default SyncHistoryTable;
